import { useState, useEffect } from 'react';
import { FiUser, FiShield, FiUsers, FiEye, FiEyeOff, FiSave, FiX, FiCheck, FiAlertTriangle } from 'react-icons/fi';
import { userAPI as usersApi } from '../../api/users';
import { authAPI } from '../../api/auth';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Button } from '../ui/Button/Button';
import { Card } from '../ui/Card/Card';
import Modal from '../Modal/Modal';
import styles from './UserProfileSettings.module.css';

import { logger } from '../../utils/logger';

interface UserProfileSettingsProps {
  onClose?: () => void;
}

interface Preferences {
  timezone: string;
  language: string;
}

export default function UserProfileSettings({ onClose }: UserProfileSettingsProps) {
  const [activeTab, setActiveTab] = useState('preferences');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  // Preferences
  const [preferences, setPreferences] = useState<Preferences>({
    timezone: 'UTC',
    language: 'en',
  });

  // Password Change
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Organizations
  const { organizations, currentOrganization: currentOrg, switchOrganization } = useOrganization();
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user profile
      const userDetails = await usersApi.getCurrentUser();
      setIsGoogleUser((userDetails.auth_provider || 'email') === 'google');
      setPreferences({
        timezone: userDetails.timezone || 'UTC',
        language: userDetails.locale || 'en',
      });
    } catch (err) {
      setError('Failed to load user data');
      logger.error('Error loading user data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    try {
      setSaving(true);
      setError(null);

      await authAPI.updateProfile({
        timezone: preferences.timezone,
        locale: preferences.language,
      });

      setSuccess('Preferences saved successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err: any) {
      const errorMessage = err.message || 'Failed to save preferences';
      setError(errorMessage);
      logger.error('Error saving preferences:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      setSwitchingOrgId(orgId);
      setError(null);
      await switchOrganization(orgId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to switch organization');
      logger.error('Error switching organization:', err);
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      
      if (passwordForm.newPassword.length < 8) {
        setError('New password must be at least 8 characters long');
        return;
      }
      
      setSaving(true);
      setError(null);
      
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      
      setSuccess('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to change password';
      setError(errorMessage);
      logger.error('Error changing password:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderPreferencesTab = () => (
    <Card className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <h3>Preferences</h3>
        <p className={styles.tabDescription}>
          Customize your interface and regional settings
        </p>
      </div>

      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Timezone</label>
          <select
            value={preferences.timezone}
            onChange={(e) => setPreferences({ ...preferences, timezone: e.target.value })}
            className={styles.select}
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
            <option value="Europe/London">London</option>
            <option value="Europe/Paris">Paris</option>
            <option value="Asia/Tokyo">Tokyo</option>
            <option value="Asia/Shanghai">Shanghai</option>
            <option value="Australia/Sydney">Sydney</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Language</label>
          <select
            value={preferences.language}
            onChange={(e) => setPreferences({ ...preferences, language: e.target.value })}
            className={styles.select}
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
            <option value="it">Italian</option>
            <option value="pt">Portuguese</option>
            <option value="zh">Chinese</option>
            <option value="ja">Japanese</option>
          </select>
        </div>
      </div>

      {!isGoogleUser && (
        <div className={styles.passwordSection}>
          <div className={styles.settingItem}>
            <div className={styles.settingInfo}>
              <h4>Password</h4>
              <p>Change your account password</p>
            </div>
            <Button
              onClick={() => setShowPasswordModal(true)}
              variant="secondary"
              size="sm"
            >
              <FiShield />
              Change Password
            </Button>
          </div>
        </div>
      )}

      <div className={styles.formActions}>
        <Button
          onClick={handleSavePreferences}
          variant="primary"
          loading={saving}
          className={styles.saveButton}
        >
          <FiSave />
          Save Preferences
        </Button>
      </div>
    </Card>
  );

  const renderOrganizationsTab = () => (
    <Card className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <h3>Organizations</h3>
        <p className={styles.tabDescription}>
          Manage your organization memberships and switch between organizations
        </p>
      </div>
      
      <div className={styles.organizationsList}>
        {organizations.map((org) => (
          <div key={org.id} className={`${styles.organizationItem} ${currentOrg?.id === org.id ? styles.current : ''}`}>
            <div className={styles.orgInfo}>
              <h4>{org.name}</h4>
              <div className={styles.orgMeta}>
                <span className={styles.orgRole}>Role: {org.user_role || 'member'}</span>
                {org.member_count != null && <span className={styles.orgMembers}>Members: {org.member_count}</span>}
              </div>
            </div>
            <div className={styles.orgActions}>
              {currentOrg?.id === org.id && (
                <span className={styles.currentBadge}>Current</span>
              )}
              {currentOrg?.id !== org.id && (
                <Button
                  onClick={() => void handleSwitchOrganization(org.id)}
                  variant="secondary"
                  size="sm"
                  disabled={switchingOrgId !== null}
                  loading={switchingOrgId === org.id}
                >
                  Switch To
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );

  const renderPasswordModal = () => (
    <Modal
      isOpen={showPasswordModal}
      onClose={() => setShowPasswordModal(false)}
      title="Change Password"
      size="medium"
    >
      <div className={styles.passwordModal}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Current Password</label>
          <div className={styles.passwordInput}>
            <input
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              className={styles.input}
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
              className={styles.passwordToggle}
            >
              {showPasswords.current ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>New Password</label>
          <div className={styles.passwordInput}>
            <input
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              className={styles.input}
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
              className={styles.passwordToggle}
            >
              {showPasswords.new ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>Confirm New Password</label>
          <div className={styles.passwordInput}>
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              className={styles.input}
            />
            <button
              type="button"
              onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
              className={styles.passwordToggle}
            >
              {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>
        </div>
        
        <div className={styles.modalActions}>
          <Button
            onClick={() => setShowPasswordModal(false)}
            variant="secondary"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="primary"
            loading={saving}
          >
            Change Password
          </Button>
        </div>
      </div>
    </Modal>
  );

  const tabs = [
    { id: 'preferences', label: 'Preferences', icon: FiUser },
    { id: 'organizations', label: 'Organizations', icon: FiUsers }
  ];

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <span>Loading profile...</span>
      </div>
    );
  }

  return (
    <div className={styles.userProfileSettings}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1>Profile Settings</h1>
          <p>Manage your account settings and preferences</p>
        </div>
        {onClose && (
          <Button
            onClick={onClose}
            variant="secondary"
            size="sm"
            className={styles.closeButton}
          >
            <FiX />
            Close
          </Button>
        )}
      </div>

      {(error || success) && (
        <div className={`${styles.notification} ${error ? styles.error : styles.success}`}>
          {error ? <FiAlertTriangle /> : <FiCheck />}
          <span>{error || success}</span>
        </div>
      )}

      <div className={styles.content}>
        <div className={styles.sidebar}>
          <nav className={styles.tabNav}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
              >
                <tab.icon className={styles.tabIcon} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className={styles.main}>
          {activeTab === 'preferences' && renderPreferencesTab()}
          {activeTab === 'organizations' && renderOrganizationsTab()}
        </div>
      </div>

      {renderPasswordModal()}
    </div>
  );
}
